import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import {
  AuthorizationSet,
  createSuccessResultVoid,
  IProfileDto,
  isSelectCompanyOrRoleButtonActiveAtom,
  localStorageHelpers,
  Log,
  profileModalOpenStateAtom,
  profileScopeAtom,
  ProfileStorage,
  SimpleResult,
  simpleResultAuthenticationError,
  subdomainIsolationCompanyIdAtom,
  systemSettingsDtoAtom,
} from "@ryde/shared";

import { useLoggedInRef } from "../../auth-profile/use-logged-in-ref";
import { useCustomTranslationsActions } from "../../i18n/use-custom-translations-actions";
import {
  findRestoredAuthSet,
  getCompanyIdFromLoginReturnTo,
  isTestOrDevelopHostOrEnv,
  resolveAuthSetsBySubdomain,
} from "./company-scope.utils";

export const useCompanyScopeSelect = () => {
  const isLoggedInRef = useLoggedInRef();
  const { handleCustomTranslations } = useCustomTranslationsActions();

  const setProfileScope = useSetRecoilState(profileScopeAtom);
  const openProfileModal = useSetRecoilState(profileModalOpenStateAtom);
  const setSubdomainIsolationCompanyId = useSetRecoilState(
    subdomainIsolationCompanyIdAtom,
  );
  const setShowCompanySelectButton = useSetRecoilState(
    isSelectCompanyOrRoleButtonActiveAtom,
  );
  const setSystemSettingsData = useSetRecoilState(systemSettingsDtoAtom);

  const clearPendingCompanyScope = useEvent((): void => {
    ProfileStorage.clearScope();
    setProfileScope(undefined);
    setSystemSettingsData(undefined);
  });

  /** Resolves false when a later selection superseded this one; it then leaves the scope to that selection. */
  const defineAndStoreCompanyScope = useEvent(
    async (scope: AuthorizationSet): Promise<boolean> => {
      Log.debug(`Setting company scope for ${scope?.companyName}`, scope);
      ProfileStorage.authSet = scope;

      // Before the scope is set, so the company's screens render with its labels from the start.
      if (scope?.companyId) {
        const isCurrentSelection = await handleCustomTranslations(
          scope.companyId,
        );

        if (!isCurrentSelection) {
          Log.debug(`Company scope for ${scope.companyName} superseded`);
          return false;
        }
      }

      setProfileScope(scope);
      return true;
    },
  );

  const applyCompanyScope = useEvent(
    async (
      scope: AuthorizationSet,
      afterCb?: () => void | Promise<void>,
    ): Promise<void> => {
      Log.debug("Applying company scope", scope);
      const isApplied = await defineAndStoreCompanyScope(scope);

      if (!isApplied) {
        return;
      }

      await afterCb?.();
    },
  );

  const openCompanySelectDialog = useEvent(() => {
    clearPendingCompanyScope();
    setShowCompanySelectButton(true);
    openProfileModal(true);
  });

  const handleSubdomainMode = useEvent(
    async ({
      subdomainAuthSets,
      subdomainCompanyId,
    }: {
      subdomainAuthSets: AuthorizationSet[];
      subdomainCompanyId: number;
    }): Promise<SimpleResult<void>> => {
      if (!subdomainAuthSets?.length) {
        return simpleResultAuthenticationError("No auth sets by subdomain");
      }

      setSubdomainIsolationCompanyId(subdomainCompanyId);

      const singleAuthSet =
        subdomainAuthSets.length === 1 ? subdomainAuthSets[0] : null;

      if (singleAuthSet) {
        await defineAndStoreCompanyScope(singleAuthSet);
        Log.info(
          `Subdomain: auto-selected company: ${singleAuthSet.companyName}, role: ${singleAuthSet.role}`,
        );
        return createSuccessResultVoid();
      }

      const stored = ProfileStorage.authSet;
      const previousAuthSet = findRestoredAuthSet({
        authSets: subdomainAuthSets,
        stored,
      });

      if (previousAuthSet) {
        await defineAndStoreCompanyScope(previousAuthSet);
        Log.info(
          `Subdomain: reused previous scope for ${previousAuthSet.companyName}`,
        );
        return createSuccessResultVoid();
      }

      Log.debug(
        "Subdomain: multiple auth sets, showing selection dialog",
        subdomainAuthSets,
      );
      openCompanySelectDialog();
      return createSuccessResultVoid();
    },
  );

  const handleSpaMode = useEvent(
    async (authSets: AuthorizationSet[]): Promise<SimpleResult<void>> => {
      if (!authSets?.length) {
        return simpleResultAuthenticationError("No auth sets in SPA mode");
      }

      const singleAuthSet = authSets.length === 1 ? authSets[0] : null;

      if (singleAuthSet) {
        await defineAndStoreCompanyScope(singleAuthSet);
        Log.debug("SPA: auto-selected single company scope", singleAuthSet);
        return createSuccessResultVoid();
      }

      const stored = ProfileStorage.authSet;
      const restored = findRestoredAuthSet({ authSets, stored });

      if (restored) {
        await defineAndStoreCompanyScope(restored);
        Log.debug("SPA: reused stored company scope", restored);
        return createSuccessResultVoid();
      }

      Log.debug(
        `SPA: multiple auth sets found (${authSets.length}), opening dialog`,
        authSets,
      );
      openCompanySelectDialog();
      return createSuccessResultVoid();
    },
  );

  const setOrChooseProfile = useEvent(
    async (profile: IProfileDto | undefined): Promise<SimpleResult<void>> => {
      if (!isLoggedInRef.current) {
        return simpleResultAuthenticationError("User not logged in");
      }

      if (!profile) {
        return simpleResultAuthenticationError("Missing profile data");
      }

      const {
        authorizedIn: authSets,
        subDomainCompanyId: subDomainCompanyIdFromBe,
      } = profile;

      if (!authSets?.length) {
        return simpleResultAuthenticationError("No auth sets in profile");
      }

      ProfileStorage.prepareScopeForUserLogin(profile.id);

      const storedScope = ProfileStorage.authSet;

      if (
        storedScope &&
        !findRestoredAuthSet({ authSets, stored: storedScope })
      ) {
        clearPendingCompanyScope();
      }

      // Deep-link override: if the user was redirected to /login from a
      // /c/:companyId/... URL, `ProtectedRoute` wrote that path to
      // localStorage `returnTo`. If the user has at least one role for that
      // company, auto-select a role (preferring the stored authSet if it
      // matches, else the first) and skip the choose-company dialog. Mirrors
      // CompanyRedirectWrapper's logic so the other roles remain available via
      // the company-switch button.
      const returnToCompanyId = getCompanyIdFromLoginReturnTo();

      // Always clear returnTo once consumed so a later setOrChooseProfile run
      // (or the next login) doesn't reuse a stale value — even if no matching
      // authSet was found below.
      if (returnToCompanyId !== null) {
        localStorageHelpers.setItem("returnTo", null);
      }

      const setsForReturnToCompany = returnToCompanyId
        ? authSets.filter(x => x.companyId === returnToCompanyId)
        : [];

      if (setsForReturnToCompany.length > 0) {
        const stored = ProfileStorage.authSet;
        const matchedStored =
          stored?.companyId === returnToCompanyId
            ? findRestoredAuthSet({ authSets: setsForReturnToCompany, stored })
            : undefined;
        const candidate = matchedStored ?? setsForReturnToCompany[0];

        setShowCompanySelectButton(authSets.length > 1);
        openProfileModal(false);
        await defineAndStoreCompanyScope(candidate);
        Log.info(
          `Auto-selected role for returnTo company ${returnToCompanyId} (${matchedStored ? "stored" : "first"}); skipping dialog`,
        );
        return createSuccessResultVoid();
      }

      const { subdomainAuthSets, subdomainCompanyId } =
        resolveAuthSetsBySubdomain({
          subDomainCompanyIdFromBe,
          authSets,
        });

      if (subdomainAuthSets.length && subdomainCompanyId) {
        return handleSubdomainMode({ subdomainAuthSets, subdomainCompanyId });
      }

      if (!isTestOrDevelopHostOrEnv()) {
        return simpleResultAuthenticationError(
          "No auth sets matched, and not a dev/test env",
        );
      }

      Log.info("SPA mode fallback active (dev or QA)");
      setShowCompanySelectButton(authSets.length > 1);
      return handleSpaMode(authSets);
    },
  );

  return {
    setOrChooseProfile,
    defineAndStoreCompanyScope,
    applyCompanyScope,
  };
};
